import * as moment from 'moment-timezone';

export type MomentType = moment.Moment | Date | number;

export class MomentUtils {

    static currentYear(): number {
        return moment().year();
    }

    static difference(moment1: moment.Moment, moment2: moment.Moment, ignoreTime?: boolean): number {
        const momentAdapter1 = MomentAdapter.of(moment1);
        const momentAdapter2 = MomentAdapter.of(moment2);
        if (ignoreTime) {
            momentAdapter1.resetTime();
            momentAdapter2.resetTime();
        }
        return momentAdapter2.get().diff(momentAdapter1.get());
    }

    static toMoment(value?: MomentType): moment.Moment {
        if (! value) {
            return moment().locale('fr');
        } else if (value instanceof moment) {
            return (value as moment.Moment).clone().locale('fr');
        } else {
            return moment(value).locale('fr');
        }
    }

    /**
     * Convert the end date of an all day event between fullcalendar and backend
     * Format on full calendar : the next day at midnight (ex: 23/05/2020 00:00:00)
     * Format on backend : the current day at 23:59 (ex: 22/05/2020 23:59:00)
     * @param endDate end date of an all day event
     * @param endDateConversion fullcalendar -> backend or backend -> fullcalendar
     */
    static convertEndDateAllDayEvent(endDate: MomentType, endDateConversion: EndDateConversion): moment.Moment {
        switch (endDateConversion) {
            case EndDateConversion.FullCalendarToBackend:
                return MomentAdapter.of(endDate).resetTime().subtractMinutes(1).get(); // Ex: 23/05/2020 00:00:00 --> 22/05/2020 23:59:00
            case EndDateConversion.BackendToFullCalendar:
                return MomentAdapter.of(endDate).addDays(1).resetTime().get(); // Ex: 22/05/2020 23:59:00 --> 23/05/2020 00:00:00
        }
    }

    static months(locale: string): string[]{
        return MomentAdapter.current(locale).months();
    }
}

export enum EndDateConversion {
    FullCalendarToBackend,
    BackendToFullCalendar
}

export class MomentAdapter {

    private readonly moment: moment.Moment;

    static of(value: MomentType, locale?: string): MomentAdapter {
        return new MomentAdapter(value, locale);
    }

    static current(locale?: string): MomentAdapter {
        return new MomentAdapter(null, locale);
    }

    static fromDuration(durationInMilliseconds: number): MomentAdapter {
        return new MomentAdapter(moment.utc(moment.duration(durationInMilliseconds, 'milliseconds').asMilliseconds()));
    }

    private constructor(value?: MomentType, locale?: string) {
        this.moment = MomentUtils.toMoment(value).tz('Europe/Paris', false);
        if (locale) {
            this.locale(locale ? locale : 'fr');
        }
    }

    locale(locale: string) {
        this.moment.locale(locale);
        return this;
    }

    addDays(days: number) {
        this.moment.add(days, 'days');
        return this;
    }

    addMonths(months: number) {
        this.moment.add(months, 'months');
        return this;
    }

    subtractDays(days: number) {
        this.moment.subtract(days, 'days');
        return this;
    }

    subtractYears(years: number) {
        this.moment.subtract(years, 'years');
        return this;
    }

    addHours(hours: number) {
        this.moment.add(hours, 'hours');
        return this;
    }

    addMinutes(minutes: number) {
        this.moment.add(minutes, 'minutes');
        return this;
    }

    subtractMinutes(minutes: number) {
        this.moment.subtract(minutes, 'minutes');
        return this;
    }

    addMilliseconds(milliseconds: number) {
        this.moment.add(milliseconds, 'milliseconds');
        return this;
    }

    goToNextQuarterOfHour() {
        const nextQuarter = this.nextQuarterOfHour();
        this.resetSeconds();
        if (nextQuarter === 60) {
            this.addHours(1);
            this.moment.set('minutes', 0);
        } else {
            this.moment.set('minutes', nextQuarter);
        }
        return this;
    }

    resetTime() {
        this.moment.set('hours', 0);
        this.moment.set('minutes', 0);
        this.resetSeconds();
        this.moment.set('milliseconds', 0);
        return this;
    }

    setEndOfDay() {
        this.moment.set('hours', 23);
        this.moment.set('minutes', 59);
        this.resetSeconds();
        this.moment.set('milliseconds', 0);
        return this;
    }

    resetSeconds() {
        this.moment.set('seconds', 0);
        return this;
    }

    time(): number {
        return this.moment.valueOf();
    }

    month() {
        return this.moment.get('month');
    }

    months() {
        return this.moment.localeData().months();
    }

    year() {
        return this.moment.get('year');
    }

    date() {
        return this.moment.get('date');
    }

    weekYears() {
        return this.moment.get('weekYears');
    }

    formatDDMMYYY(): string {
        return this.format('DD/MM/YYYY');
    }

    format(specificFormat?: string): string {
        return this.moment.format(specificFormat ? specificFormat : 'DD/MM/YYYY HH:mm:ss');
    }

    get(): moment.Moment {
        return this.moment;
    }

    private nextQuarterOfHour(): number {
        const minutes = this.moment.get('minutes');
        if (minutes === 0) {
            return 0;
        }
        if (minutes - 15 <= 0) {
            return 15;
        }
        if (minutes - 30 <= 0) {
            return 30;
        }
        if (minutes - 45 <= 0) {
            return 45;
        }
        return 60; // Next hour
    }

    setYear(year: number) {
        this.moment.set('years', year);
        return this;
    }

    setHour(hour: number) {
        this.moment.set('hours', hour);
        return this;
    }

    setMinute(minute: number) {
        this.moment.set('minutes', minute);
        return this;
    }

    setMonth(month: number) {
        this.moment.set('months', month);
        return this;
    }

    setDay(day: number) {
        this.moment.set('date', day);
        return this;
    }
}
